// Iter-207v — Monthly Cabinet Statement email template settings (CEO only).
//
// Single-page editor for the 5 keys driving the "État mensuel" email :
//   • monthly_statement_email_from_alias        (Workspace "Send mail as" address)
//   • monthly_statement_email_to_default        (default To, comma-separated)
//   • monthly_statement_email_cc_default        (default Cc, comma-separated)
//   • monthly_statement_email_subject_template  (Jinja-like {{var}})
//   • monthly_statement_email_body_template     (Jinja-like {{var}})
//
// Variables : {{period}} {{total_expenses}} {{total_payments}} {{net}} {{sender_name}}.
import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Loader2, Mail } from "lucide-react";
import { Button } from "../components/ui/button";

const VARS = [
  { name: "{{period}}", desc: "Période (MM/YYYY)" },
  { name: "{{total_expenses}}", desc: "Total dépenses du mois (formaté)" },
  { name: "{{total_payments}}", desc: "Total règlements du mois (formaté)" },
  { name: "{{net}}", desc: "Net (règlements − dépenses)" },
  { name: "{{sender_name}}", desc: "Nom de l'envoyeur connecté" },
];


export default function MonthlyStatementEmailSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    monthly_statement_email_from_alias: "",
    monthly_statement_email_to_default: "",
    monthly_statement_email_cc_default: "",
    monthly_statement_email_subject_template: "",
    monthly_statement_email_body_template: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/settings");
        setForm({
          monthly_statement_email_from_alias: r.data?.monthly_statement_email_from_alias || "",
          monthly_statement_email_to_default: r.data?.monthly_statement_email_to_default || "",
          monthly_statement_email_cc_default: r.data?.monthly_statement_email_cc_default || "",
          monthly_statement_email_subject_template: r.data?.monthly_statement_email_subject_template || "",
          monthly_statement_email_body_template: r.data?.monthly_statement_email_body_template || "",
        });
      } catch (e) {
        toast.error("Chargement des settings échoué");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Template enregistré");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-sm text-zinc-500">Chargement…</div>;

  return (
    <div className="max-w-3xl space-y-5" data-testid="monthly-statement-email-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-zinc-900 tracking-tight flex items-center gap-2">
          <Mail size={20} /> État mensuel — Email
        </h1>
        <p className="text-xs text-zinc-500 mt-1">
          Template par défaut utilisé lors de l'envoi de l'état mensuel (PDF + Excel + CSV en pièce jointe).
          Surchargable à chaque envoi.
        </p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-5 space-y-4">
        <Field label="From (alias Workspace)" hint="Doit être configuré dans Gmail « Send mail as »." testid="from-alias">
          <input
            type="text" value={form.monthly_statement_email_from_alias}
            onChange={(e) => setForm({ ...form, monthly_statement_email_from_alias: e.target.value })}
            data-testid="settings-from-alias"
            className="w-full h-9 px-2 border border-zinc-200 rounded-md text-sm"
          />
        </Field>
        <Field label="To par défaut" hint="Séparés par virgule" testid="to-default">
          <input
            type="text" value={form.monthly_statement_email_to_default}
            onChange={(e) => setForm({ ...form, monthly_statement_email_to_default: e.target.value })}
            data-testid="settings-to-default"
            className="w-full h-9 px-2 border border-zinc-200 rounded-md text-sm"
          />
        </Field>
        <Field label="Cc par défaut" hint="Séparés par virgule (optionnel)" testid="cc-default">
          <input
            type="text" value={form.monthly_statement_email_cc_default}
            onChange={(e) => setForm({ ...form, monthly_statement_email_cc_default: e.target.value })}
            data-testid="settings-cc-default"
            className="w-full h-9 px-2 border border-zinc-200 rounded-md text-sm"
          />
        </Field>
        <Field label="Sujet" testid="subject-template">
          <input
            type="text" value={form.monthly_statement_email_subject_template}
            onChange={(e) => setForm({ ...form, monthly_statement_email_subject_template: e.target.value })}
            data-testid="settings-subject-template"
            className="w-full h-9 px-2 border border-zinc-200 rounded-md text-sm font-mono"
          />
        </Field>
        <Field label="Corps du message" testid="body-template">
          <textarea
            value={form.monthly_statement_email_body_template}
            onChange={(e) => setForm({ ...form, monthly_statement_email_body_template: e.target.value })}
            rows={10}
            data-testid="settings-body-template"
            className="w-full px-2 py-1.5 border border-zinc-200 rounded-md text-sm font-mono"
          />
        </Field>
        <div className="border-t border-zinc-100 pt-3">
          <div className="text-xs font-medium text-zinc-700 mb-1.5">Variables disponibles :</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {VARS.map((v) => (
              <div key={v.name} className="text-xs">
                <code className="font-mono bg-zinc-100 text-zinc-800 px-1 py-0.5 rounded">{v.name}</code>
                <span className="text-zinc-500 ml-2">{v.desc}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}


const Field = ({ label, hint, children, testid }) => (
  <div data-testid={`settings-field-${testid}`}>
    <label className="text-xs font-medium text-zinc-700 block mb-1">{label}</label>
    {children}
    {hint && <div className="text-[11px] text-zinc-400 mt-1">{hint}</div>}
  </div>
);
